import { t, Instance, flow } from 'mobx-state-tree';
import { LifeEventModel, createLifeEventModelFromResponse } from './models/LifeEvent';
import { personLifeEventsApi } from '@/api';
import type { LifeEventResponse, CreateLifeEventRequest, UpdateLifeEventRequest } from 'we-tree-api-client/v2';

/**
 * PersonLifeEventsStore - Manages life events for family members.
 * All API calls go through the SDK via personLifeEventsApi.
 */
export const PersonLifeEventsStore = t
  .model('PersonLifeEventsStore', {
    eventsByFamilyMember: t.map(t.map(LifeEventModel)),
    loadingStates: t.map(t.boolean),
    errors: t.map(t.string)
  })
  .views((self) => ({
    getLifeEventsForFamilyMember(familyMemberId: string): Instance<typeof LifeEventModel>[] {
      const map = self.eventsByFamilyMember.get(familyMemberId);
      if (!map) return [];
      return Array.from(map.values());
    },

    getLifeEventById(lifeEventId: string): Instance<typeof LifeEventModel> | undefined {
      for (const map of self.eventsByFamilyMember.values()) {
        const ev = map.get(lifeEventId);
        if (ev) return ev;
      }
      return undefined;
    },

    isLoadingForFamilyMember(familyMemberId: string): boolean {
      return self.loadingStates.get(familyMemberId) ?? false;
    },

    getErrorForFamilyMember(familyMemberId: string): string | null {
      return self.errors.get(familyMemberId) ?? null;
    }
  }))
  .actions((self) => ({
    clearLifeEventsForFamilyMember(familyMemberId: string) {
      self.eventsByFamilyMember.delete(familyMemberId);
      self.loadingStates.delete(familyMemberId);
      self.errors.delete(familyMemberId);
    },

    setLoading(familyMemberId: string, isLoading: boolean) {
      if (isLoading) {
        self.loadingStates.set(familyMemberId, true);
      } else {
        self.loadingStates.delete(familyMemberId);
      }
    },

    setError(familyMemberId: string, errorMessage: string | null) {
      if (errorMessage) {
        self.errors.set(familyMemberId, errorMessage);
      } else {
        self.errors.delete(familyMemberId);
      }
    },

    loadLifeEventsForFamilyMember: flow(function* (familyMemberId: string) {
      self.loadingStates.set(familyMemberId, true);
      self.errors.delete(familyMemberId);

      try {
        const result = yield personLifeEventsApi.listLifeEventsForPerson(familyMemberId);

        if (result.success) {
          const events = result.data?.events ?? [];
          let map = self.eventsByFamilyMember.get(familyMemberId);
          if (!map) {
            self.eventsByFamilyMember.set(familyMemberId, {} as Record<string, never>);
            map = self.eventsByFamilyMember.get(familyMemberId)!;
          } else {
            map.clear();
          }
          events.forEach((ev: LifeEventResponse) => {
            try {
              map.set(ev.id, createLifeEventModelFromResponse(ev));
            } catch (err) {
              // eslint-disable-next-line no-console
              console.error('[PersonLifeEventsStore] Failed to create life event model:', {
                ev,
                error: err instanceof Error ? err.message : String(err)
              });
            }
          });
          self.errors.delete(familyMemberId);
        } else {
          const msg = result.error?.getUserMessage?.() ?? result.error?.message ?? 'Failed to load life events';
          self.errors.set(familyMemberId, msg);
        }
      } catch (error) {
        const msg = error instanceof Error ? error.message : 'An unexpected error occurred';
        self.errors.set(familyMemberId, msg);
        // eslint-disable-next-line no-console
        console.error('[PersonLifeEventsStore] Error loading life events:', error);
      } finally {
        self.loadingStates.delete(familyMemberId);
      }
    }),

    createLifeEvent: flow(function* (request: CreateLifeEventRequest) {
      try {
        const result = yield personLifeEventsApi.createLifeEvent(request);
        if (result.success && result.data) {
          const familyMemberId = request.familyMemberId;
          let map = self.eventsByFamilyMember.get(familyMemberId);
          if (!map) {
            self.eventsByFamilyMember.set(familyMemberId, {} as Record<string, never>);
            map = self.eventsByFamilyMember.get(familyMemberId)!;
          }
          map.set(result.data.id, createLifeEventModelFromResponse(result.data));
          return { success: true as const, data: result.data };
        }
        return { success: false as const, error: result.error };
      } catch (error) {
        return {
          success: false as const,
          error: error instanceof Error ? error : new Error('Failed to create life event')
        };
      }
    }),

    updateLifeEvent: flow(function* (lifeEventId: string, request: UpdateLifeEventRequest) {
      try {
        const result = yield personLifeEventsApi.updateLifeEvent(lifeEventId, request);
        if (result.success && result.data) {
          const ev = self.getLifeEventById(lifeEventId);
          const oldPids = ev?.participantIds ?? (ev ? [ev.familyMemberId] : []);
          const newPids = result.data.participantIds ?? (ev ? [ev.familyMemberId] : []);
          applyParticipantUpdate(self, lifeEventId, oldPids, newPids, result.data);
          return { success: true as const, data: result.data };
        }
        return { success: false as const, error: result.error };
      } catch (error) {
        return {
          success: false as const,
          error: error instanceof Error ? error : new Error('Failed to update life event')
        };
      }
    }),

    deleteLifeEvent: flow(function* (lifeEventId: string) {
      const ev = self.getLifeEventById(lifeEventId);
      if (!ev) {
        return { success: false as const, error: new Error('Life event not found in store') };
      }
      try {
        const result = yield personLifeEventsApi.deleteLifeEvent(lifeEventId);
        if (result.success) {
          const map = self.eventsByFamilyMember.get(ev.familyMemberId);
          if (map) map.delete(lifeEventId);
          return { success: true as const };
        }
        return { success: false as const, error: result.error };
      } catch (error) {
        return {
          success: false as const,
          error: error instanceof Error ? error : new Error('Failed to delete life event')
        };
      }
    }),

    addParticipant: flow(function* (lifeEventId: string, familyMemberId: string) {
      const oldEv = self.getLifeEventById(lifeEventId);
      if (!oldEv) {
        return { success: false as const, error: new Error('Life event not found in store') };
      }
      try {
        const result = yield personLifeEventsApi.addParticipant(lifeEventId, { familyMemberId });
        if (result.success && result.data) {
          applyParticipantUpdate(self, lifeEventId, oldEv.participantIds, result.data.participantIds, result.data);
          return { success: true as const, data: result.data };
        }
        return { success: false as const, error: result.error };
      } catch (error) {
        return {
          success: false as const,
          error: error instanceof Error ? error : new Error('Failed to add participant')
        };
      }
    }),

    deleteParticipant: flow(function* (lifeEventId: string, familyMemberId: string) {
      const oldEv = self.getLifeEventById(lifeEventId);
      if (!oldEv) {
        return { success: false as const, error: new Error('Life event not found in store') };
      }
      try {
        const result = yield personLifeEventsApi.deleteParticipant(lifeEventId, familyMemberId);
        if (result.success && result.data) {
          applyParticipantUpdate(self, lifeEventId, oldEv.participantIds, result.data.participantIds, result.data);
          return { success: true as const, data: result.data };
        }
        return { success: false as const, error: result.error };
      } catch (error) {
        return {
          success: false as const,
          error: error instanceof Error ? error : new Error('Failed to remove participant')
        };
      }
    })
  }));

function applyParticipantUpdate(
  self: { eventsByFamilyMember: { get(key: string): unknown; set(key: string, value: unknown): void } },
  lifeEventId: string,
  oldPids: string[],
  newPids: string[],
  data: LifeEventResponse
): void {
  // First, remove the event from all old participant maps to avoid node conflicts
  // We'll add it back to the new maps below
  for (const pid of oldPids) {
    const map = self.eventsByFamilyMember.get(pid) as Map<string, Instance<typeof LifeEventModel>> | undefined;
    if (map) map.delete(lifeEventId);
  }

  // Now add the event to each new participant's map with a fresh instance
  // Each map gets its own instance to avoid MST "node exists twice" errors
  for (const pid of newPids) {
    let map = self.eventsByFamilyMember.get(pid) as Map<string, Instance<typeof LifeEventModel>> | undefined;
    if (!map) {
      self.eventsByFamilyMember.set(pid, {} as Record<string, never>);
      map = self.eventsByFamilyMember.get(pid) as Map<string, Instance<typeof LifeEventModel>>;
    }
    // Create a new instance for this map - MST allows separate instances with the same data
    map.set(lifeEventId, createLifeEventModelFromResponse(data));
  }
}

export type PersonLifeEventsStoreType = Instance<typeof PersonLifeEventsStore>;

export const personLifeEventsStore = PersonLifeEventsStore.create({});
